<template>
  <div class="fatigue-index-card">
    <div class="fatigue-index-card__levels">
      <div
          v-for="level in normalizedLevels"
          :key="level.key"
          class="fatigue-index-card__level-row"
      >
        <div class="fatigue-index-card__level-name">
          <span
              class="fatigue-index-card__mark"
              :style="{ backgroundColor: level.color }"
          ></span>

          <span>{{ level.label }}</span>
        </div>

        <span
            class="fatigue-index-card__change"
            :class="{
              'positive-change': level.change > 0,
              'negative-change': level.change < 0,
              'neutral-change': level.change === 0
            }"
        >
          {{ getPercentChangeText(level.change) }}
        </span>

        <span class="fatigue-index-card__percent">
          {{ level.value }}%
        </span>
      </div>
    </div>

    <div
        class="fatigue-index-card__bar"
        :class="{ 'fatigue-index-card__bar--empty': !hasLevelSegments }"
    >
      <template v-if="hasLevelSegments">
        <div
            v-for="level in levelSegments"
            :key="level.key"
            class="fatigue-index-card__bar-part"
            :style="{
              width: level.percent + '%',
              backgroundColor: level.color
            }"
        ></div>
      </template>
    </div>

    <div
        v-if="hasLevelSegments"
        class="fatigue-index-card__bar-values"
    >
      <span
          v-for="level in levelSegments"
          :key="level.key"
          class="fatigue-index-card__bar-value"
          :style="{ width: level.percent + '%' }"
      >
        {{ level.value }}%
      </span>
    </div>

    <div class="fatigue-index-card__positions">
      <div class="fatigue-index-card__subtitle">
        Хроническая усталость по должностям
      </div>

      <div class="fatigue-index-card__position-list">
        <div
            v-for="position in normalizedPositions"
            :key="position.name"
            class="fatigue-index-card__position-row"
        >
          <div class="fatigue-index-card__position-name">
            <span>{{ position.name }}</span>

            <a-popover
                v-if="position.warning"
                trigger="hover"
                placement="top"
            >
              <template #content>
                <div class="fatigue-index-card__popover">
                  {{ position.warning }}
                </div>
              </template>

              <InfoCircleOutlined class="fatigue-index-card__warning-icon" />
            </a-popover>
          </div>

          <div class="fatigue-index-card__mini-scale">
            <span
                v-for="dot in position.max"
                :key="dot"
                class="fatigue-index-card__mini-dot"
                :class="{ 'fatigue-index-card__mini-dot--active': dot <= position.value }"
                :style="dot <= position.value ? { backgroundColor: position.color } : null"
            ></span>
          </div>

          <div class="fatigue-index-card__position-value">
            <span>{{ position.value }}</span>
            <span>/ {{ position.max }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { InfoCircleOutlined } from '@ant-design/icons-vue'

export default {
  name: 'ChronicFatigueIndexCard',

  components: {
    InfoCircleOutlined,
  },

  props: {
    levels: {
      type: Array,
      default: () => [],
    },
    positions: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const toNumber = (value) => {
      const number = Number(value)

      return Number.isFinite(number) ? number : 0
    }

    const normalizedLevels = computed(() => {
      return props.levels.map((level) => ({
        ...level,
        change: toNumber(level.change),
        value: toNumber(level.value),
      }))
    })

    const levelsTotal = computed(() => {
      return normalizedLevels.value.reduce((sum, level) => {
        return sum + level.value
      }, 0)
    })

    const hasLevelSegments = computed(() => {
      return levelsTotal.value > 0
    })

    const levelSegments = computed(() => {
      if (!levelsTotal.value) {
        return []
      }

      return normalizedLevels.value
          .filter(level => level.value > 0)
          .map(level => ({
            ...level,
            percent: Number(((level.value / levelsTotal.value) * 100).toFixed(2)),
          }))
    })

    const normalizedPositions = computed(() => {
      return props.positions.map((position) => {
        const value = toNumber(position.value)
        const max = toNumber(position.max) || 10

        return {
          ...position,
          value: Math.min(value, max),
          max,
        }
      })
    })

    const getPercentChangeText = (value) => {
      if (value > 0) {
        return `↑ ${value}%`
      }

      if (value < 0) {
        return `↓ ${Math.abs(value)}%`
      }

      return '–'
    }

    return {
      normalizedLevels,
      levelSegments,
      hasLevelSegments,
      normalizedPositions,
      getPercentChangeText,
    }
  },
}
</script>

<style scoped lang="scss">
.fatigue-index-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.fatigue-index-card__levels {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}

.fatigue-index-card__level-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 52px 42px;
  align-items: center;
  column-gap: 10px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.fatigue-index-card__level-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.fatigue-index-card__level-name span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fatigue-index-card__mark {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  background: var(--main-green);
  border-radius: 2px;
}

.fatigue-index-card__change,
.fatigue-index-card__percent {
  text-align: right;
  white-space: nowrap;
}

.fatigue-index-card__percent {
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-index-card__bar {
  display: flex;
  width: 100%;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  gap: 2px;
}

.fatigue-index-card__bar--empty {
  background: #d9dee7;
}

.fatigue-index-card__bar-part {
  height: 100%;
  min-width: 0;
}

.fatigue-index-card__bar-values {
  display: flex;
  width: 100%;
  margin-top: 6px;
  font-size: 11px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.fatigue-index-card__bar-value {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fatigue-index-card__positions {
  margin-top: 26px;
}

.fatigue-index-card__subtitle {
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-index-card__position-list {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.fatigue-index-card__position-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 94px 46px;
  align-items: center;
  column-gap: 12px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.fatigue-index-card__position-name {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.fatigue-index-card__position-name span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fatigue-index-card__warning-icon {
  color: #FAAD14;
  font-size: 13px;
  flex: 0 0 auto;
  cursor: pointer;
}

.fatigue-index-card__popover {
  max-width: 240px;
  font-size: 13px;
  line-height: 130%;
}

.fatigue-index-card__mini-scale {
  display: grid;
  grid-template-columns: repeat(10, 3px);
  gap: 2px;
  justify-content: end;
}

.fatigue-index-card__mini-dot {
  width: 3px;
  height: 8px;
  border-radius: 999px;
  background: #EEF0F3;
}

.fatigue-index-card__position-value {
  display: flex;
  justify-content: flex-end;
  gap: 3px;
  white-space: nowrap;
}

.fatigue-index-card__position-value span:first-child {
  color: rgba(0, 0, 0, 0.88);
}

.fatigue-index-card__position-value span:last-child {
  color: rgba(0, 0, 0, 0.45);
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}

@media (max-width: $mobile-breakpoint) {
  .fatigue-index-card__position-row {
    grid-template-columns: minmax(0, 1fr) auto;
    row-gap: 4px;
  }

  .fatigue-index-card__mini-scale {
    grid-column: 1;
    justify-content: start;
  }

  .fatigue-index-card__position-value {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
}
</style>